import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import { useMemo, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import StartTrackingStatusChip from "./StartTrackingStatusChip";
import GoalsDataDisclaimer from "../GoalsDataDisclaimer";
import {
  alignForPercent,
  computeStartTrackingMetrics,
} from "./startTrackingMetrics";

function Triangle({ direction = "down", sx = {} }) {
  const isUp = direction === "up";
  return (
    <Box
      aria-hidden
      sx={{
        width: 0,
        height: 0,
        borderLeft: "6px solid transparent",
        borderRight: "6px solid transparent",
        ...(isUp
          ? { borderBottom: "8px solid #2D2D2D" }
          : { borderTop: "8px solid #2D2D2D" }),
        ...sx,
      }}
    />
  );
}

/**
 * Hoverable Marker block:
 * - marker is anchored at left: xx% (no transform)
 * - we create a hover "hitbox" that includes label + triangle
 * - on hover: raise zIndex so everything pops over neighbors
 * - faint label background always
 */
function Marker({
  id,
  label,
  sublabel,
  leftPercent,
  position = "above",
  anchor = "percent",
  align = "center",
  tooltipText = "",
  hoveredId,
  setHoveredId,
}) {
  const isAbove = position === "above";
  const isHovered = hoveredId === id;

  const left = anchor === "fillEnd" ? "100%" : `${leftPercent}%`;

  const labelTransform =
    align === "left"
      ? "translateX(0)"
      : align === "right"
        ? "translateX(-100%)"
        : "translateX(-50%)";

  // relative to bar-top overlay
  const triangleTop = isAbove ? -10 : 30;
  const labelTop = isAbove ? -63 : 39;

  // create a hover area that covers both label + triangle
  const hitboxTop = isAbove ? -64 : 24; // covers triangle+label region
  const hitboxHeight = isAbove ? 70 : 70; // tune if you want bigger hover zone

  return (
    <Box
      sx={{
        position: "absolute",
        left,
        top: 0,
        pointerEvents: "auto",
        zIndex: isHovered ? 9 : 2,
      }}
      onMouseEnter={() => setHoveredId(id)}
      onMouseLeave={() => setHoveredId(null)}
    >
      {/* HITBOX: invisible but hoverable area */}
      <Box
        sx={{
          position: "absolute",
          top: hitboxTop,
          left: 0,
          transform: "translateX(-50%)",
          width: 220,
          height: hitboxHeight,
        }}
      />

      {/* Triangle pinned at anchor */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: triangleTop,
          left: 0,
          transform: "translateX(-50%)",
          pointerEvents: "none",
        }}
      >
        <Triangle direction={isAbove ? "down" : "up"} />
      </Box>

      {/* Label (always faint background) */}
      <Box
        sx={{
          position: "absolute",
          top: labelTop,
          left: 0,
          transform: labelTransform,

          whiteSpace: "nowrap",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          alignItems:
            align === "left"
              ? "flex-start"
              : align === "right"
                ? "flex-end"
                : "center",

          px: 1,
          py: 0.75,
          borderRadius: "8px",
          bgcolor: "rgba(255,255,255,0.88)",
          boxShadow: isHovered
            ? "0 10px 22px rgba(0,0,0,0.18)"
            : "0 2px 10px rgba(0,0,0,0.10)",

          transition: "box-shadow 150ms ease",
          // pointerEvents: "none",
        }}
      >
        <Stack
          direction="row"
          mx="auto"
          alignItems="center"
          textAlign="center"
          justifyContent="center"
          spacing={0.5}
        >
          <Typography variant="body2" sx={{ fontSize: 12, color: "#454545" }}>
            {label}
          </Typography>
          <Tooltip
            title={tooltipText}
            placement="right"
            slotProps={{
              popper: {
                modifiers: [{ name: "offset", options: { offset: [0, -10] } }],
              },
              tooltip: {
                sx: {
                  bgcolor: "#FFFFFF",
                  color: "#2D2D2D",
                  border: "1px solid #E6E6E6",
                  boxShadow: "0px 1px 2px 0px #0000000D",
                },
              },
            }}
          >
            <Box
              aria-label="Info about region rank"
              sx={{ lineHeight: 0, display: "inline-flex" }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 15, color: "#A0A0A0" }} />
            </Box>
          </Tooltip>
        </Stack>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, fontSize: 12, color: "#454545" }}
        >
          {sublabel}
        </Typography>
      </Box>
    </Box>
  );
}

export default function StartTrackingCard({
  title = "Start Tracking",
  buttonText = "Track Starts",
  status = "",
  goal = 0,
  progress = 0,
  todaysBenchmark = 0,
  onTrackStarts,
  estimatedHitGoal = -1,
  metricLabel = "Starts",
  progressTooltipText = "The total starts count for your region in the current quarter.",

  products = [],
  selectedProduct = "",
  onProductChange,
  disableProductSelect = false,

  showDropdown,

  noGoalsData = false,
  goalsDataDisclaimerShow = false,
}) {
  const { safeGoal, safeProgress, safeBenchmark, progressPct, benchmarkPct } =
    computeStartTrackingMetrics(goal, progress, todaysBenchmark);

  const [hoveredId, setHoveredId] = useState(null);

  const BAR_HEIGHT = 30;
  const BAR_PAD_Y = 32; // py:4 => 32px with default spacing

  const benchAlign = useMemo(() => {
    if (benchmarkPct === 100) return "right";
    return alignForPercent(benchmarkPct);
  }, [benchmarkPct]);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "8px",
        borderColor: "#F0F0F0",
        boxShadow: "1px 4px 4px 0px #00000008",
        overflow: "visible",
        minHeight: "376px",
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 3, overflow: "visible" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}
            >
              {title}
            </Typography>
            {!!status && !noGoalsData && (
              <StartTrackingStatusChip status={status} />
            )}
          </Stack>

          {showDropdown && (
            <TextField
              select
              size="small"
              value={selectedProduct}
              onChange={(e) => onProductChange?.(e.target.value)}
              sx={{
                width: 235,
                height: 30,
                "& .MuiInputBase-root": { height: 30 },
              }}
              InputProps={{ sx: { borderRadius: 1 } }}
              SelectProps={{
                inputProps: { "aria-label": "Product" },
                displayEmpty: false,
              }}
              disabled={disableProductSelect}
            >
              {products.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        {goalsDataDisclaimerShow && <GoalsDataDisclaimer />}

        <>
          <Box
            sx={{
              position: "relative",
              mx: "35px",
              overflow: "visible",
              mt: goalsDataDisclaimerShow ? "70px" : "100px",
            }}
          >
            {/* Bar with padding */}
            <Box sx={{ py: 4 }}>
              <Box
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={safeGoal}
                aria-valuenow={safeProgress}
                sx={{
                  height: BAR_HEIGHT,
                  borderRadius: "2px",
                  overflow: "hidden",
                  bgcolor: "#F1F1F1",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    width: `${progressPct}%`,
                    height: "100%",
                    bgcolor: "#D96B04",
                    transition: "width 300ms ease",
                  }}
                />
              </Box>
            </Box>

            {/* Overlay anchored at bar top */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: BAR_PAD_Y,
                height: BAR_HEIGHT,
                zIndex: 2,
                overflow: "visible",
                pointerEvents: "auto",
              }}
            >
              {!noGoalsData && (
                <Marker
                  id="benchmark"
                  tooltipText="Expected cumulative progress toward the quarterly goal as determined by days elapsed versus total days in the quarter."
                  label="Today's Benchmark:"
                  sublabel={`${safeBenchmark} ${metricLabel}`}
                  leftPercent={benchmarkPct}
                  position="above"
                  align={benchAlign}
                  hoveredId={hoveredId}
                  setHoveredId={setHoveredId}
                />
              )}
              {!noGoalsData && (
                <Marker
                  id="goal"
                  tooltipText="The total quarterly goal value for your region."
                  label="Goal:"
                  sublabel={`${safeGoal} ${metricLabel}`}
                  leftPercent={100}
                  position="above"
                  align="right"
                  hoveredId={hoveredId}
                  setHoveredId={setHoveredId}
                />
              )}

              <Marker
                id="progress"
                tooltipText={progressTooltipText}
                label="Progress:"
                sublabel={`${safeProgress} ${metricLabel}`}
                leftPercent={progressPct}
                position="below"
                align={alignForPercent(progressPct)}
                hoveredId={hoveredId}
                setHoveredId={setHoveredId}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: "54px" }}>
            <Stack>
              {!noGoalsData && estimatedHitGoal !== -1 && (
                <Typography
                  sx={{
                    textAlign: "center",
                    backgroundColor: "#F7F9FB",
                    p: "8px 10px",
                    mb: 2,
                    fontSize: "14px",
                    weight: 400,
                    fontFamily: '"Helvetica Neue", Arial, sans-serif',
                  }}
                >
                  Referrals needed to hit goal:&nbsp;
                  <Typography
                    component="span"
                    sx={{ fontWeight: 700, fontSize: "14px" }}
                  >
                    {estimatedHitGoal}
                  </Typography>
                </Typography>
              )}
              <Button
                variant="contained"
                onClick={onTrackStarts}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "4px",
                  px: 3,
                  py: 1,
                  bgcolor: "#5E889C",
                  "&:hover": { bgcolor: "#507485" },
                  height: "32px",
                }}
              >
                {buttonText}
              </Button>
            </Stack>
          </Box>
        </>
      </CardContent>
    </Card>
  );
}
